// Module 2 / Slice 9 — terminology editor.
// Plan §10 screen 2. Locale-pivoted view with per-tenant override.
// V1 ships read + locale-switch; inline-edit + bulk-save lands in a
// follow-up commit (the wire-level PUT contract is already locked in
// reference.yaml; the SPA just needs the form scaffold).

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { referenceApiClient } from "../../api/client";
import { useI18n } from "../../i18n/I18nContext";

const SupportedLocales = ["en-US", "es"] as const;

export function TerminologyEditorScreen(): JSX.Element {
  const { t } = useI18n();
  const [locale, setLocale] = useState<(typeof SupportedLocales)[number]>("en-US");

  const query = useQuery({
    queryKey: ["reference", "terminology", locale],
    queryFn: async () => {
      const response = await referenceApiClient.GET("/reference/terminology", {
        params: { query: { locale } },
      });
      if (!response.data) {
        throw new Error("reference_terminology_fetch_failed");
      }
      return response.data;
    },
  });

  return (
    <div>
      <h1>{t("reference.terminology.heading")}</h1>
      <p className="rc-help">{t("reference.terminology.description")}</p>
      <div role="tablist" aria-label={t("reference.terminology.localeTabs")}>
        {SupportedLocales.map((l) => (
          <button
            key={l}
            type="button"
            role="tab"
            aria-selected={locale === l}
            onClick={() => setLocale(l)}
            className={locale === l ? "rc-tab rc-tab--active" : "rc-tab"}
          >
            {l}
          </button>
        ))}
      </div>
      {query.isLoading && <p>{t("login.discovering")}</p>}
      {query.isError && (
        <p role="alert" style={{ color: "var(--rc-color-danger)" }}>
          {(query.error as Error).message}
        </p>
      )}
      {query.data && (
        <table className="rc-table">
          <thead>
            <tr>
              <th>{t("reference.terminology.column.resourceKey")}</th>
              <th>{t("reference.terminology.column.default")}</th>
              <th>{t("reference.terminology.column.override")}</th>
              <th>{t("reference.terminology.column.resolved")}</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(query.data.defaults ?? {}).map(([key, defaultValue]) => {
              const override = query.data?.overrides?.[key];
              const resolved = query.data?.resolved?.[key] ?? defaultValue;
              return (
                <tr key={key}>
                  <td><code>{key}</code></td>
                  <td>{String(defaultValue)}</td>
                  <td>{override !== undefined ? <em>{String(override)}</em> : "\u2014"}</td>
                  <td><strong>{String(resolved)}</strong></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
